import {
  Auth0Client,
  type User,
} from "@auth0/auth0-spa-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AuthState = {
  loading: boolean;
  configured: boolean;
  authenticated: boolean;
  error?: string;
  user?: User;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  accessToken: () => Promise<string>;
  retry: () => void;
};

const AuthContext = createContext<AuthState | undefined>(undefined);
const AUTH_INITIALIZATION_TIMEOUT_MS = 8_000;
const AUTH_CALLBACK_TIMEOUT_MS = 30_000;
const e2eAuthEnabled =
  (import.meta as unknown as { env?: Record<string, string | boolean> }).env?.DEV ===
    true &&
  (import.meta as unknown as { env?: Record<string, string | boolean> }).env
    ?.VITE_E2E_AUTH === "true";

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string) {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const rejected = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => reject(new Error(`${label}_timeout`)), timeoutMs);
  });
  return Promise.race([promise, rejected]).finally(() => {
    if (timeout) clearTimeout(timeout);
  });
}

export function HaloIdProvider({ children }: { children: React.ReactNode }) {
  const [client, setClient] = useState<Auth0Client>();
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState<string>();
  const [user, setUser] = useState<User>();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    setConfigured(false);
    if (e2eAuthEnabled) {
      setConfigured(true);
      setAuthenticated(true);
      setUser({
        sub: "auth0|e2e-user",
        name: "Test KOLIVO",
        email: "test@kolivo.invalid",
      });
      setLoading(false);
      return () => {
        active = false;
      };
    }
    void (async () => {
      const response = await withTimeout(
        fetch("/api/v1/public/config", {
          headers: { accept: "application/json" },
          cache: "no-store",
        }),
        AUTH_INITIALIZATION_TIMEOUT_MS,
        "public_config"
      );
      if (!response.ok) throw new Error(`public_config_${response.status}`);
      const config = (await response.json()) as {
        haloId: null | { issuer: string; audience: string; clientId: string };
      };
      if (!config.haloId) {
        throw new Error("haloid_not_configured");
      }
      const auth0 = new Auth0Client({
        domain: new URL(config.haloId.issuer).hostname,
        clientId: config.haloId.clientId,
        cacheLocation: "memory",
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
        useCookiesForTransactions: true,
        authorizeTimeoutInSeconds: 8,
        httpTimeoutInSeconds: 30,
        authorizationParams: {
          audience: config.haloId.audience,
          connection: "Username-Password-Authentication",
          redirect_uri: window.location.origin,
          scope: "openid profile email",
        },
      });
      if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
        await withTimeout(
          auth0.handleRedirectCallback(),
          AUTH_CALLBACK_TIMEOUT_MS,
          "redirect_callback"
        );
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // Safari can block the silent HaloID iframe. It must never keep the
        // entire application on a permanent splash screen.
        await withTimeout(
          auth0.checkSession(),
          AUTH_INITIALIZATION_TIMEOUT_MS,
          "check_session"
        ).catch(() => undefined);
      }
      if (!active) return;
      setClient(auth0);
      setConfigured(true);
      const signedIn = await withTimeout(
        auth0.isAuthenticated(),
        AUTH_INITIALIZATION_TIMEOUT_MS,
        "authentication_status"
      );
      setAuthenticated(signedIn);
      if (signedIn) setUser(await auth0.getUser());
    })().catch(() => {
      if (active) {
        setError(
          "HaloID n’a pas pu terminer la vérification de sécurité. Réessayez ou rechargez la page."
        );
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [attempt]);

  const login = useCallback(async () => {
    await client?.loginWithRedirect({
      authorizationParams: {
        connection: "Username-Password-Authentication",
        redirect_uri: window.location.origin,
      },
      appState: { returnTo: window.location.pathname },
    });
  }, [client]);
  const logout = useCallback(async () => {
    await client?.logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }, [client]);
  const accessToken = useCallback(async () => {
    if (e2eAuthEnabled) return "e2e-access-token";
    if (!client) throw new Error("HaloID n’est pas configuré.");
    return client.getTokenSilently();
  }, [client]);
  const retry = useCallback(() => {
    if (
      window.location.search.includes("code=") ||
      window.location.search.includes("state=") ||
      window.location.search.includes("error=")
    ) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    setAttempt(value => value + 1);
  }, []);

  const value = useMemo(
    () => ({
      loading,
      configured,
      authenticated,
      error,
      user,
      login,
      logout,
      accessToken,
      retry,
    }),
    [loading, configured, authenticated, error, user, login, logout, accessToken, retry]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useHaloId() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("HaloIdProvider manquant");
  return value;
}
